"use client";
import Image from "next/image";
import React, { useId, useState } from "react";
import Button from "../ui/Button";
import { motion, AnimatePresence } from "framer-motion";
import ContactModal from "../Home/ContactModal";
import { useGetFaqs } from "@/actions/faq";

interface FaqProps {
  stickyNote?: boolean;
  /** Page/category slug to fetch FAQs for. Defaults to the site-wide "general" FAQ. */
  slug?: string;
}

const Faq: React.FC<FaqProps> = ({ stickyNote = true, slug }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openItems, setOpenItems] = useState<number[]>([]);
  const { faqs, faqsLoading, faqsEmpty } = useGetFaqs(slug);
  // Prefix for the question/answer id pairs the accordion's ARIA wiring needs.
  const baseId = useId();

  const toggleItem = (id: number) => {
    setOpenItems((prev) =>
      prev.includes(id)
        ? prev.filter((itemId) => itemId !== id)
        : [...prev, id],
    );
  };

  if (faqsEmpty) {
    return null;
  }

  return (
    <section className="w-full bg-white min-h-auto px-4 pt-8 pb-11 sm:py-15 relative">
      <div className="max-w-290.75 mx-auto flex flex-col">
        <h2 className="text-center font-red-hat text-[#1B365E] text-[30px] sm:text-[40px] font-light leading-10 sm:leading-13.25 tracking-[0.3px]">
          FAQ
        </h2>

        <p
          className={`${!stickyNote && "w-full max-w-214.25 mx-auto"} mt-4 md:mt-11 mb-8 sm:mb-6 text-[#1D1D1D] text-[20px] leading-7.5 tracking-[0.3px] font-medium`}
        >
          Nous avons apporté les réponses aux questions les plus posées sur les
          formations :
        </p>

        <div className="flex md:flex-row flex-col items-start justify-between gap-8 md:gap-6 w-full">
          <div
            className={`${!stickyNote && "mx-auto"} w-full max-w-214.25 flex flex-col gap-4`}
          >
            {faqsLoading &&
              Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="h-12 sm:h-14 bg-[#EFEFEF] animate-pulse rounded-lg"
                />
              ))}

            {!faqsLoading && faqs.map((item) => {
              const isOpen = openItems.includes(item.id);
              const questionId = `${baseId}-question-${item.id}`;
              const answerId = `${baseId}-answer-${item.id}`;
              return (
                <div
                  key={item.id}
                  className="bg-[#EFEFEF] rounded-lg overflow-hidden transition-all duration-300 ease-in-out"
                >
                  <button
                    type="button"
                    id={questionId}
                    aria-expanded={isOpen}
                    aria-controls={answerId}
                    className="w-full flex justify-between items-center px-4 lg:px-6 py-3 sm:py-2.5 bg-transparent border-none cursor-pointer text-left transition-all duration-200 ease-in-out hover:bg-[#D9D9D9]"
                    onClick={() => toggleItem(item.id)}
                  >
                    <span className="text-[#1D1D1D] text-[12px] sm:text-[16px] leading-4 sm:leading-5 font-bold tracking-[0.3px]">
                      {item.question}
                    </span>
                    <motion.span
                      aria-hidden="true"
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="ml-4 text-[25px] md:text-[50px] lg:text-[58px] leading-4 md:leading-11.25 font-red-hat font-semibold text-[#1D1D1D] inline-block h-fit"
                    >
                      {isOpen ? "−" : "+"}
                    </motion.span>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        id={answerId}
                        role="region"
                        aria-labelledby={questionId}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 lg:px-6 lg:pt-2.5 pb-3 sm:pb-5">
                          <p className="text-[#1D1D1D] text-[12px] leading-5">
                            {item.answer}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

          <div
            className={`${!stickyNote && "hidden"} min-w-59 w-full md:w-59 border border-[#FFBC42] rounded-2xl pt-6 sm:pt-5.75 pb-6.25 px-8 md:px-5 flex items-center flex-col sticky top-4`}
          >
            <Image
              src="/assets/icons/faqs-right-img.svg"
              width={84}
              height={24}
              alt="Questions FAQ"
              title="Questions FAQ"
              className="md:w-21 w-17.25"
            />
            <p className="text-[#1B365E] text-[14px] leading-5 font-normal tracking-[0.3px] text-center py-4">
              Des questions <br className="md:block hidden" />
              sur les formations ?
            </p>
            <Button
              variant="outline-gradient"
              onClick={() => setIsModalOpen(true)}
              className="w-full"
            >
              Nous contacter
            </Button>
          </div>
        </div>
      </div>
      <ContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </section>
  );
};

export default Faq;
